import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import {
    Afr, DimensionReference, FactTable, FactTableRow, MatchTableFacts, generateLocalStateFromEventProperties,
    getDimensionForFact, getSelectedAfr, getTable, clearMatchingTableFacts
} from '../../redux/AfrSlice';
import { loadConcept } from '../../redux/ConceptSlice';
import { setIsDirty } from '../../redux/MiscSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion, TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import { ErrorSummaryAFRSection, getErrorList } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper, { AccountRangeFilter } from '../../utils/utilityHelper';
import SelectNewAccountModal from '../layout/Modal/SelectNewAccountModal';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';

export interface DimensionColumnSpecs {
    axis: string,
    title: string,
    codeLabelMap: any
}

export interface IAccountEditorGridNoSubaccountsOptions {
    gridname: string;
    cellEditHandler: any,
    doneFlag: string,
    noFlag: string,
    fixedRows: boolean,
    firstFieldId: string,
    allowedCombinations: Array<string> | null,
    dimensionColumn: DimensionColumnSpecs | null,
    accountRangeFilter: AccountRangeFilter | null,
    accountGroups: any | null
}
export const fillFixedRows = function (acctTable: FactTable,
    dimensionColumnAxis: string,
    options: IAccountEditorGridNoSubaccountsOptions,
    tabledata: any[],
    dataColumns: []
) {
    const columnAxis = "FundGroupsAxis"; // Should derive from structure
    var allowedCells: string[] | null = options.allowedCombinations;
    var dimColumnSpecs: DimensionColumnSpecs | null = options.dimensionColumn;
    var rowMapping: any = {};
    var rowDimensions: Array<DimensionReference> = [];
    // If there are row dimensions then we need to extract them
    if (acctTable.structure.rowHierarchy.axes && acctTable.structure.rowHierarchy.axes.length > 0) {
        for (var rowAxis of acctTable.structure.rowHierarchy.axes) {
            if (dimensionColumnAxis && (rowAxis.id === dimensionColumnAxis)) {
                // For now only process one axis - the one designated in the options
                // can be expanded to multiple axes later.
                for (var memConcept of rowAxis.membersByYear) {
                    rowDimensions.push({ axis: rowAxis, member: memConcept, typedContent: null });
                }
            }
        }
    }
    var nextRow: any = {};
    for (var rowConcept of acctTable.structure.rows) {
        var accountLabel = rowConcept.name;
        if (accountLabel.indexOf("Title") >= 0) {
            continue;
        }
        // Now expand the rows on the dimensions (if any)
        if (rowDimensions.length > 0) {
            // Only process the first level for now
            for (var rowDim of rowDimensions) {
                nextRow = {
                    account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
                }
                if (dimensionColumnAxis && options.dimensionColumn && rowDim.member != null
                    && options.dimensionColumn.codeLabelMap.hasOwnProperty(rowDim.member.code)) {
                    var rid = rowConcept.id + "|" + rowDim.member.id;
                    var testid = rowConcept.code + "|" + rowDim.member.id;
                    if (!allowedCells || (allowedCells.indexOf(testid) >= 0)) {
                        var lbl = options.dimensionColumn.codeLabelMap[rowDim.member.code];
                        nextRow[dimensionColumnAxis] = lbl;
                        nextRow["dimensionCode"] = rowDim.member.code;
                        tabledata.push(nextRow);
                        rowMapping[rid] = nextRow;
                    }
                }
            }
        } else {
            nextRow = {
                account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
            }
            tabledata.push(nextRow);
            rowMapping[rowConcept.id] = nextRow;
        }
    }
    if (dimColumnSpecs) {
        // We have a table with a column representing a dimension on the fact. The column values will be the labels of the dimension identified by the dimension
        // column axis. 
        // Currently this is used by the DataElementWorksheet table.
        // Collect all the facts and then reassign to the rows created above
        var allFacts : any[] = [];
        for (var row of acctTable.rows) {
            for (var f of row.facts) {
                allFacts.push(f);
            }
        }
        for (var fTable of allFacts) {
            var factColDim = getDimensionForFact(fTable, dimensionColumnAxis);
            if (!factColDim) {
                // todo throw an error
                continue;
            }
            var accountId = fTable.concept.id + "|" + factColDim.id;
            if (rowMapping.hasOwnProperty(accountId)) {
                var nextRow = rowMapping[accountId];
                var colcode = "amount";
                nextRow[colcode] = fTable.value;
                if (options.dimensionColumn) {
                    var memName = factColDim.code;
                    var lbl = dimColumnSpecs.codeLabelMap[memName];
                    nextRow[dimColumnSpecs.axis] = lbl;
                }
            }
        }
    } else {
        for (var row of acctTable.rows) {
            if (row !== null && row.account !== null) {
                var accountId = row.account.id;
                if (rowMapping.hasOwnProperty(accountId)) {
                    var nextRow = rowMapping[accountId];
                    if (dataColumns.length > 1) {
                        for (var fact of row.facts) {
                            var dimensionForFact = getDimensionForFact(fact, columnAxis);
                            if (dimensionForFact != null) {
                                var colcode = dimensionForFact.id;
                                nextRow[colcode] = fact.value;
                            }                           
                        }

                    } else {
                        for (var fact of row.facts) {
                            var colcode = "amount";
                            nextRow[colcode] = fact.value;
                        }
                    }
                }
            }         
        }
    }
}

function shouldExcludeCode(code: string, filter: AccountRangeFilter): boolean {
    if (code < filter.first) {
        return true;
    }
    if (code > filter.last) {
        return true;
    }
    return false;
}

export function shouldExcludeRow(row: FactTableRow, filter: AccountRangeFilter | null): boolean {
    if (!filter) {
        return false;
    }
    if (!row.account || !row.account.code) {
        return false;
    }
    return shouldExcludeCode(row.account.code, filter);
}
const AccountEditorGridNoSubaccounts = React.memo(
    function AccountEditorGrid(props: {
        tableName: string,
        gridData: any[], gridColumns: any[], gridOptions: any,
        options: IAccountEditorGridNoSubaccountsOptions
    }) {

        /* ************************************** Redux ********************************************/
        const dispatch = useAppDispatch();
        var tabulatorRef = React.useRef<any>(null);
        const fieldToEdit = useRef(props.options.firstFieldId)
        const parameters = useLocation();
        const errorList = useAppSelector(getErrorList);
        var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
        const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
        /* ************************************** Modal section ********************************************/

        const [modalAccountIsOpen, setModalAccountIsOpen] = useState(false);
        const [dataKey, setDataKey] = useState("");
        const [deleteRequest, setDeleteRequest] = useState<MatchTableFacts | null>(null);
        const [hiddenColumnMap, setHiddenColumnMap] = useState<any>({});

        /* *************************************************************************************************/

        const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
        const afrYear = currentAfr ? currentAfr.year : 2019;

        /* *************************************************************************************************/

        const handleCloseAccountModal = function (account: any) {
            if ((typeof account) === "string") {
                setDataKey(account);
                fieldToEdit.current = "FG_001_GeneralFundMember";
            }
            setModalAccountIsOpen(false);
        }


        function isDoneOrNo() {
            //TODO this single function is being invoked in too many other
            //places.  A state variable should be used.
            var isDone = true;
            var flag: boolean | null = null;
            var eventNo = generateLocalStateFromEventProperties(currentAfr, [props.options.noFlag]);
            if (props.options.doneFlag === "na") {
                /*console.log("dont check done");*/
                flag = (eventNo[props.options.noFlag] ? true : false) || utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                    || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin );
            } else {
                /*console.log("check done", props.options.doneFlag);*/
                var eventDone = generateLocalStateFromEventProperties(currentAfr, [props.options.doneFlag]);
                /*console.log("dont check done");*/
                flag = (eventDone[props.options.doneFlag] ? true : false) || (eventNo[props.options.noFlag] ? true : false) || utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                    || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin);
            }


            // NOTE - for new AFRs these flags will be null.
            // Since there may be no assignment true or false, we check for null
            return flag ? flag : false;
        }

        const getEntityCodeFromUrl = function () {
            var returnValue = "";
            if (parameters && parameters.state && parameters.state.entityCode) { return parameters.state.entityCode }
            return returnValue;
        }


        const showErrorIcon = function (cell: any, formatterParams: any, onRendered: any) {
            var text: string = cell.getValue();
            var { section, tab } = utilityHelper.getSectionError(props.tableName);

            if (section.length === 0 && tab.length === 0) {
                return text;
            }

            if ((typeof text) === "undefined") {
                return text;
            }

            // Verify if there is an error in this section
            var errorSection: ErrorSummaryAFRSection = errorList.find((x:any) => x.section.sectionCode === section);
            if ((typeof errorSection) === "undefined") {
                return text;
            }

            // Discover the array of errors for the section
            var error = errorSection.errorList.find(x => x.entityType === tab && x.entityCode === cell.getData().accountCode);

            if ((typeof error) === "undefined") {
                return text;
            } else {
                if (error !== undefined) {
                    return "<i class='bi bi-x-octagon-fill text-danger' title='" + error.description + "'></i> " + text;
                }
            }
            

        };


        const getCellAddress = function (cell: any) {
            var row = cell.getRow();
            if (row._row.type != "row") { return "na" }
            const rowCode = row.getData().accountCode;
            const fld = cell.getField();
            return rowCode + "|" + fld;
        }

        // special logic to verify if a cell is editable or not
        // editable    --> return true
        // no editable --> return false
        const handleEditCheck = function (cell: any) {
            if (isDoneOrNo()) {
                return false;
            }
            if (cell.getField() === "amount") {
                return true;
            }
            const allowedList = props.options.allowedCombinations;
            if (allowedList) {
                const cellAddr: string = getCellAddress(cell);
                if (allowedList.indexOf(cellAddr) >= 0) {
                    return true;
                } else {
                    return false;
                }
            }
            return true;
        }

        const handleHeaderClick = function (e: any, column: any) {
            // get the column name to pull the concept associated to it
            var fundCode = column.getField();
            dispatch(loadConcept({
                conceptId: fundCode,
                taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                afrYear: currentAfr? currentAfr.year : 2019}));

        }
        const handleCellClick = function (e: any, cell: any) {
            //e - the click event object
            //cell - cell component
            var row = cell.getRow();
            if (row._row.type != "row") { return }

            //var table = cell.getTable();
            if (!props.options.fixedRows && !cell) {
                row.scrollTo();
                //table.scrollToRow(row.getData().accountCode, "center", true);
            }
            var fld = cell.getField();
            if (fld === "FundGroupsAxis") {
                var dimId = row.getData().dimensionCode;
                if (dimId) {
                    dispatch(loadConcept({
                        conceptId: "FundCode_" + dimId,
                        taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                        afrYear: afrYear
                    }));
                }
            } else {
                var accountId = row.getData().accountCode;
                if (accountId) {
                    dispatch(loadConcept({
                        conceptId: "AccountCode_" + accountId,
                        taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                        afrYear: afrYear
                    }));
                }
            }
        }
        const handleDeleteCellClick = function (event: any, cell: any) {
            var row: any = cell.getRow();
            var rowData = row.getData();
            var rowToSelect = row.getPrevRow();
            if (!rowToSelect) {
                rowToSelect = row.getNextRow();
            }
            var nextAccountCode = "";
            if (rowToSelect) {
                nextAccountCode = rowToSelect.getData().accountCode;
            }
            //           row.getTable().deselectRow();
            var rowMatch: MatchTableFacts = {
                tableName: props.tableName,
                matchConcept: {
                    conceptId: rowData.accountId,
                    dimensions: [],
                    conceptName: nextAccountCode,
                    taxonomyType: utilityHelper.getTaxonomyTypeFromVersion(versionInfo),
                    afrYear: currentAfr !== null ? currentAfr.year : 0
                }
            }
            event.stopPropagation();
            setDeleteRequest(rowMatch);
        }

        // logic to handle the dirty flag in redux store
        const handleEdited = function (cell: any) {

            var newValue: string = cell.getValue();
            newValue = newValue.trim();

            if ((utilityHelper.getFloatValue(cell.getOldValue()) != utilityHelper.getFloatValue(newValue)) || (newValue.length > 0 && utilityHelper.getFloatValue(newValue) === 0.0)) {
                // Turn ON the dirty flag in redux
                dispatch(setIsDirty({ IsDirty: true, Page: "" }));
                setDataKey(cell.getRow().getData().accountCode);
                // get next cell to right
                fieldToEdit.current = getNextCell(cell);
                props.options.cellEditHandler(cell);
            }

        }


        // handle the logic to add a new account 
        const handleAddAccount = function (e: any, column: any) {
            if (isDoneOrNo() === false) {
                column.getTable().deselectRow();
                setModalAccountIsOpen(true);
            }

        }

        // calculate totals for each row
        const getTotals = function (value: any, rowData: any) {
            let total = 0;
            Object.getOwnPropertyNames(rowData).forEach(
                function (val, idx, array) {
                    // Fetch the value associated with the property and try to convert to a number
                    // NOTE - some of the row attributes are metadata, not fact values, so they need to be skipped
                    switch (val) {
                        case "account":
                        case "accountId":
                        case "accountCode":
                            break;
                        default:
                            if (utilityHelper.getFloatValue(rowData[val]) != 0) {
                                total = total + utilityHelper.getFloatValue(rowData[val]);
                            }
                            break;
                    }
                }
            );
            return total;
        }

        const gridData = function () {
            if (tabulatorRef.current && tabulatorRef.current.table) {
                var table = tabulatorRef.current.table;
                var data: any[] = table.getData();
                return data;
            }
            return null;
        }


        const handleRowClick = function (e:any, row:any) {
            //e - the click event object
            //row - row component
            if (row._row.type != "row") { return }

            row.getTable().deselectRow();
            row.select();
        }

        // Search a row component given a Account Code
        const getRowFromAccountCode = function (account: string) {
            if (account.length === 0) { return null }

            if (!(tabulatorRef.current && tabulatorRef.current.table)) {
                return null;
            }
            var rows: any[];
            var table = tabulatorRef.current.table;

            rows = table.searchRows("accountCode", "=", account);
            if (rows && rows.length > 0) {
                return rows[0];
            }

            return null;

        }

        // get next row to right to be edited
        const getNextCell = function (cell: any) {
            if (!cell) { return "" };
            
            return cell.getField();
        }


        const newGridOptions={
            layout: "fitColumns",
            columnHeaderVertAlign: "bottom",
            layoutColumnsOnNewData: true,
            dataTree: true,
            selectable: 1,
            selectableRollingSelection: false,
            height: "780",  // to be adjusted if another thing is decided
            // columnMaxWidth: 120,
            index: "accountCode",           
            cellEdited: (cell: any) => handleEdited(cell),
            cellClick: (e: any, cell: any) => handleCellClick(e, cell),
            rowClick: (e: any, cell: any) => handleRowClick(e, cell),
            ...props.gridOptions
        }

        const addAccountGroup = function (nextRow: any) {
            if (props.options.accountGroups) {
                var accountGroup = "";
                if (props.options.accountGroups.hasOwnProperty(rowConcept.code)) {
                    accountGroup = props.options.accountGroups[rowConcept.code];
                }
                nextRow["accountGroup"] = accountGroup;
            }
        }

        const addAccountTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

            //build dropdown
            var button = document.createElement("div");

            button.innerHTML = "<i class='bi bi-plus-circle' title='Add new Account...'></i>  Account Code";

            button.onclick = function () {
                //                console.log("add account dialog pops up");
                // add code here
                if (tabulatorRef.current && tabulatorRef.current.table) {
                    var table = tabulatorRef.current.table;
                    if (isDoneOrNo() === false) {
                        table.deselectRow();
                        setModalAccountIsOpen(true);
                    }
                }
            }

            return button;
        }
        const isEmptyColumn = function (tableDataRows: any[], fld: string) {
            for (var dataRow of tableDataRows) {
                if (!dataRow.hasOwnProperty(fld)) {
                    continue;
                }
                var val = dataRow[fld];
                if (val == null) {
                    continue;
                }
                return false;
            }
            return true;
        }
        const collapseEmptyColumns = function (table: any) {
            var colDefs: any[] = table.getColumnDefinitions();
            var tableRows: any[] = table.getData();
            var hcm: any = {
            }
            if (colDefs) {
                colDefs.map(cd => {
                    if (cd.hasOwnProperty("columns")) {
                        var cols: any[] = cd.columns;
                        cols.map(col => {
                            if (col.hasOwnProperty("validator") && isEmptyColumn(tableRows, col.field)) {
                                if (!hiddenColumnMap.hasOwnProperty(col.field)) { 
                                    // we are going to hide it
                                    hcm[col.field] = col.field;
                                }
                                table.toggleColumn(col.field);
                            }
                        });
                    }                    
                });
            }
            setHiddenColumnMap(hcm);
        }
        const collapseEmptyColumnsFormatter = function (cell: any, formatterParams: any, onRendered: any) {

            //build dropdown
            var button = document.createElement("div");

            button.innerHTML = "<i class='bi bi-filter-circle' title='Toggle collapse of empty columns...'></i><span> Show/Hide empty columns</span>";

            button.onclick = function () {
                collapseEmptyColumns(cell.getTable());
            }

            return button;
        }

        const plusAccountTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

            //build dropdown
            var button = document.createElement("div");

            button.innerHTML = "<i class='bi bi-plus-circle' title='Add new Account...'></i>";

            button.onclick = function () {
               
                var table = cell.getTable();
                if (isDoneOrNo() === false) {
                    table.deselectRow();
                    setModalAccountIsOpen(true);
                }              
            }

            return button;
        }


        const removeRowTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

            return "<span style='color:red'><i class='bi bi-dash-circle' title='Remove row'></i></span>";
        }

        const defaultColumns: any = [];

        var maxHeaderRows = 1;
        var acctTable: FactTable | null = null;
        if (currentAfr) {
            acctTable = getTable(currentAfr, props.tableName);

            if (acctTable) {
                for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                    if (tbeGroup.sublevels) {
                        maxHeaderRows = 2;
                    }
                }
            }
        }
        if (props.options.fixedRows) {
            if (maxHeaderRows === 1) {
                defaultColumns.push(
                    {
                        title: "Account Code",
                        field: "account",
//                        width: 280,
                        frozen: false,
                        formatter: showErrorIcon
                    }
                );
                if (props.options.dimensionColumn) {
                    defaultColumns.push(
                        {
                            title: props.options.dimensionColumn.title,
                            field: props.options.dimensionColumn.axis,
 //                           width: 180,
                            frozen: false,
                            editable: false,
                            formatter: showErrorIcon
                        }
                    );
                }
            } else {
                if (props.options.dimensionColumn) {
                    defaultColumns.push(
                        {
                            title: "",
                            headerHozAlign: "center",
                            frozen: true,
                            columns: [
                                { title: "Account Code", field: "account", width: 280 },
                                {
                                    title: props.options.dimensionColumn.title,
                                    field: props.options.dimensionColumn.axis,
                                    width: 180,
                                    frozen: true,
                                    editable: false,
                                    formatter: showErrorIcon
                                }
                            ]
                        }
                    );
                } else {
                    defaultColumns.push(
                        {
                            title: "",
                            headerHozAlign: "center",
                            frozen: true,
                            columns: [
                                { title: "Account Code", field: "account", width: 280, formatter: showErrorIcon },
                            ]
                        }
                    );
                }
            }
        } else {
            defaultColumns.push(
                {
                    title: "",
                    headerHozAlign: "center",
                    frozen: true,
                    titleFormatter: collapseEmptyColumnsFormatter,
                    columns: [
                        { title: "Account Code", field: "account", width: 280, formatter: showErrorIcon, titleFormatter: "plaintext" },
                        { title: "Account Code", field: "action", width: 25, headerSort: false, cellClick: handleDeleteCellClick, formatter: removeRowTitleFormatter, titleFormatter: plusAccountTitleFormatter }
                    ]
                }
            );
        }



        const allowedCells = props.options.allowedCombinations;
        var dataColumns : any[] = [];
        if (acctTable) {
            // Note - it is possible for the requested account table to not be present in this AFR
            // However that should be deal with upstream so the option doe snot appear
            for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                var t : any | null = null;
                var tGroup = "";
                var colGroup : any[] = [];
                if (tbeGroup.sublevels) {
                    for (var tbeCol of tbeGroup.sublevels) {
                        if (tbeCol.dimensionaspect.member != null) {
                            t = tbeCol.dimensionaspect.member.id;
                        } else {
                            t = null;
                        }
                       
                        var titletext = tbeCol.label.replace(" Funds", "").replace(" Fund", "");
                        if (allowedCells) {
                            // We don't have column sums for restricted input
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 }
                            });
                        } else {
                            newGridOptions.layout = "fitData";
                            var vis: boolean = true;
                            if (hiddenColumnMap.hasOwnProperty(t)) {
                                vis = false;
                            }
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                width: 160,
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                topCalc: "sum",
                                topCalcFormatter: "money",
                                topCalcFormatterParams: { symbol: "$", precision: 0 },
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                                visible: vis
                            });
                        }
                        dataColumns.push(t);
                    }
                    tGroup = tbeGroup.label;
                } else {
                    t = (tbeGroup.dimensionaspect != null && tbeGroup.dimensionaspect.member != null) ? tbeGroup.dimensionaspect.member.id : "amount";
                    var titletext = tbeGroup.label.replace(" Funds", "").replace(" Fund", "").replace("root", "Amount");
                    if (allowedCells) {
                        if (props.options.accountGroups) {
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                bottomCalc: "sum",
                                bottomCalcFormatter: "money",
                                bottomCalcFormatterParams: { symbol: "$", precision: 0 },
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                            });
                        } else {
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                            });
                        }
                    } else {
                        var visNoGroup: boolean = true;
                        if (hiddenColumnMap.hasOwnProperty(t)) {
                            visNoGroup = false;
                        }
                        colGroup.push({
                            title: titletext,
                            field: t,
                            headerSort: false,
                            editor: "input",
                            editable: handleEditCheck,
                            headerClick: handleHeaderClick,
                            editorParams: { mask: "*999999999999" },
                            validator: "numeric",
                            headerHozAlign: "right",
                            hozAlign: "right",
                            topCalc: "sum",
                            topCalcFormatter: "money",
                            cellEdited: handleEdited,
                            cellClick: handleCellClick,
                            topCalcFormatterParams: { symbol: "$", precision: 0 },
                            formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                            visible: visNoGroup
                        });
                    }
                    dataColumns.push(t);
                }
                if (maxHeaderRows < 2) {
                    for (var col of colGroup) {
                        defaultColumns.push(col);
                    }
                } else {
                    defaultColumns.push({
                        title: tGroup,
                        field: '',
                        headerHozAlign: "center",
                        columns: colGroup
                    });
                }
            }
        }


        // Adding Total Column - if not using restricted cell model where there are no totals
        if (!allowedCells) {
            defaultColumns.push(
                {
                    title: "",
                    headerHozAlign: "center",
                    columns: [
                        {
                            title: 'Total',
                            field: 'total',
                            headerSort: false,
                            headerHozAlign: "right",
                            hozAlign: 'right',
                            editor: false,
                            width: 200,
                            mutator: getTotals,
                            topCalc: "sum",
                            topCalcFormatter: "money",
                            topCalcFormatterParams: { symbol: "$", precision: 0 },
                            formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                        },
                    ]
                }
            );
        }


        var tabledata: any = [];
        var dimColumnSpecs = props.options.dimensionColumn;
        var dimensionColumnAxis = dimColumnSpecs ? dimColumnSpecs.axis : null;
        if (acctTable) {
            const columnAxis = "FundGroupsAxis"; // Should derive from structure
            if (props.options.fixedRows) {
                var rowMapping: any = {};
                var rowDimensions: Array<DimensionReference> = [];
                // If there are row dimensions then we need to extract them
                if (acctTable.structure.rowHierarchy.axes && acctTable.structure.rowHierarchy.axes.length > 0) {
                    for (var rowAxis of acctTable.structure.rowHierarchy.axes) {
                        if (dimensionColumnAxis && (rowAxis.id === dimensionColumnAxis)) {
                            // For now only process one axis - the one designated in the options
                            // can be expanded to multiple axes later.
                            for (var memConcept of rowAxis.membersByYear) {
                                rowDimensions.push({ axis: rowAxis, member: memConcept, typedContent: null });
                            }
                        }
                    }
                }
                var nextRow: any = {};
                for (var rowConcept of acctTable.structure.rows) {
                    var accountLabel = rowConcept.name;
                    if (accountLabel.indexOf("Title") >= 0) {
                        continue;
                    }
                    // Now expand the rows on the dimensions (if any)
                    if (rowDimensions.length > 0) {
                        // Only process the first level for now
                        for (var rowDim of rowDimensions) {
                            nextRow = {
                                account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
                            }
                            if (rowDim.member != null && dimensionColumnAxis && props.options.dimensionColumn
                                && props.options.dimensionColumn.codeLabelMap.hasOwnProperty(rowDim.member.code)) {
                                var rid = rowConcept.id + "|" + (rowDim.member != null ? rowDim.member.id : "");
                                var testid = rowConcept.code + "|" + (rowDim.member != null ? rowDim.member.id : "");
                                if (!allowedCells || (allowedCells.indexOf(testid) >= 0)) {
                                    if (rowDim.member != null) {
                                        var lbl = props.options.dimensionColumn.codeLabelMap[rowDim.member.code];
                                        nextRow[dimensionColumnAxis] = lbl;
                                        nextRow["dimensionCode"] = rowDim.member.code;
                                        addAccountGroup(nextRow);
                                        tabledata.push(nextRow);
                                        rowMapping[rid] = nextRow;
                                    }
                                }
                            }
                        }
                    } else {
                        nextRow = {
                            account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
                        }
                        addAccountGroup(nextRow);
                        tabledata.push(nextRow);
                        rowMapping[rowConcept.id] = nextRow;
                    }
                }
                if (dimColumnSpecs) {
                    // We have a table with a column representing a dimension on the fact. The column values will be the labels of the dimension identified by the dimension
                    // column axis. 
                    // Currently this is used by the DataElementWorksheet table.
                    // Collect all the facts and then reassign to the rows created above
                    var allFacts : any[] = [];
                    for (var row of acctTable.rows) {
                        for (var f of row.facts) {
                            allFacts.push(f);
                        }
                    }
                    for (var fTable of allFacts) {
                        var factColDim = getDimensionForFact(fTable, dimensionColumnAxis);
                        if (!factColDim) {
                            // todo throw an error
                            continue;
                        }
                        var accountId = fTable.concept.id + "|" + factColDim.id;
                        if (rowMapping.hasOwnProperty(accountId)) {
                            var nextRow = rowMapping[accountId];
                            var colcode = "amount";
                            nextRow[colcode] = fTable.value;
                            //                            console.log(JSON.stringify(nextRow));
                            if (props.options.dimensionColumn) {
                                var memName = factColDim.code;
                                var lbl = dimColumnSpecs.codeLabelMap[memName];
                                nextRow[dimColumnSpecs.axis] = lbl;
                            }
                        }
                    }
                } else {
                    for (var row of acctTable.rows) {
                        if (row !== null && row.account !== null) {
                            var accountId = row.account.id;
                            if (rowMapping.hasOwnProperty(accountId)) {
                                var nextRow = rowMapping[accountId];
                                if (dataColumns.length > 1) {
                                    for (var fact of row.facts) {
                                        var dimensionForFact = getDimensionForFact(fact, columnAxis);
                                        if (dimensionForFact !== null) {
                                            var colcode = dimensionForFact.id;
                                            nextRow[colcode] = fact.value;
                                        }                                       
                                    }

                                } else {
                                    for (var fact of row.facts) {
                                        var colcode = "amount";
                                        nextRow[colcode] = fact.value;
                                    }
                                }
                            }
                        }
                    }
                }
            } else {
                for (var row of acctTable.rows) {
                    if (shouldExcludeRow(row, props.options.accountRangeFilter)) {
                        continue;
                    }
                    if (row != null && row.account != null) {
                        var accountLabel = row.account.name;

                        nextRow = {
                            account: accountLabel, accountCode: row.account.code, accountId: row != null && row.account != null ? row.account.id : ""
                        }
                        for (var fact of row.facts) {
                            var dimensionForFact = getDimensionForFact(fact, columnAxis);
                            if (dimensionForFact !== null) {
                                var colcode = dimensionForFact.id;
                                nextRow[colcode] = fact.value;
                            }                            
                        }
                        tabledata.push(nextRow);
                    }                   
                }
            }

        }

        useEffect(() => {
            if (deleteRequest) {
                var dreq: MatchTableFacts = deleteRequest;
                setDeleteRequest(null);
                var nextAccountCode = dreq.matchConcept.conceptName;
                dispatch(setIsDirty({ IsDirty: true, Page: "" }));
                setDataKey(nextAccountCode);
                dispatch(clearMatchingTableFacts(dreq));
            }
        }, [deleteRequest]);

        React.useEffect(() => {
            if (tabulatorRef.current && tabulatorRef.current.table) {
                utilityHelper.scrollToTableRowByDataKey(tabulatorRef.current.table, dataKey, fieldToEdit.current);
                if (dataKey && dataKey.length > 0) {
                    var nextAccountCode = "AccountCode_" + dataKey.split("|")[0].trim();
                    dispatch(loadConcept({
                        conceptId: nextAccountCode,
                        taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                        afrYear: currentAfr ? currentAfr.year : 2019
                    }));
                }
            }
        }, [gridData()]);

        useEffect(() => {
            if (parameters.state && parameters.state.entityCode && parameters.state.entityCode.length > 0) {
                setDataKey(parameters.state.entityCode);
            }
        }, [getEntityCodeFromUrl()]);

        React.useEffect(() => {
            tabulatorRef.current = new Tabulator('#dew', {
                data: tabledata,
                columns: defaultColumns,
                layout: "fitColumns",
                columnHeaderVertAlign: "bottom",
                layoutColumnsOnNewData: true,
                dataTree: true,
                selectable: 1,
                selectableRollingSelection: false,
                height: "780",  // to be adjusted if another thing is decided
                index: "accountCode",
                groupBy: "accountGroup",
                columnCalcs: "group",
               
            }).on("rowClick", handleRowClick);


        }, [props,tabledata])

        return (

            <React.Fragment>
                <div id="dew" ref={ele => { tabulatorRef.current = ele } } />

                {acctTable && (<SelectNewAccountModal
                    tableName={props.tableName}
                    isOpen={modalAccountIsOpen}
                    onCloseModal={handleCloseAccountModal}
                    filter={props.options.accountRangeFilter}
                />)}



            </React.Fragment>
        );
    }
);

export { AccountEditorGridNoSubaccounts };